import { GraduationCap, HeartPulse, Construction, Briefcase, Shield, Leaf, Users, ScrollText, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Section, PageHero, Eyebrow } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useContent } from "@/lib/useContent";

const ICONS = { GraduationCap, HeartPulse, Construction, Briefcase, Shield, Leaf, Users, ScrollText };

export default function Priorities() {
  const { data: content } = useContent();
  if (!content) return <div className="h-screen" />;
  const priorities = content.priorities || [];

  return (
    <div>
      <PageHero eyebrow="Our Platform" title="Priorities for District 7A" subtitle="Practical, common-sense solutions focused on the people and families of Perry Hall, White Marsh, Kingsville, Middle River, and Bowleys Quarters." />

      {/* FEATURE BANNER — Public Safety */}
      <Section className="pt-14">
        <Reveal className="relative rounded-3xl overflow-hidden shadow-2xl" data-testid="priorities-safety-banner">
          <img src="/priorities-safety.webp" alt="Satish Chapagain shaking hands with a local police officer at a community event" className="w-full h-[320px] sm:h-[420px] object-cover object-[center_25%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/70 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-xl px-7 sm:px-12 text-white">
              <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-blue-300 mb-3">Public Safety</div>
              <h2 className="font-serif text-2xl sm:text-4xl font-bold leading-tight">Safe neighborhoods, built on trust</h2>
              <p className="mt-4 text-blue-100 leading-relaxed text-sm sm:text-base">Satish believes in partnership between our families and our first responders — supporting community policing, prevention, and the people who keep District 7A safe.</p>
            </div>
          </div>
        </Reveal>
      </Section>

      <Section className="py-14">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>Where Satish Stands</Eyebrow>
          <h2 className="font-serif text-3xl font-bold text-navy">Eight priorities for our community</h2>
          <p className="text-slate-600 mt-2">Tap any issue to see Satish's plan.</p>
        </Reveal>
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4" data-testid="priorities-accordion">
            {priorities.map((p, i) => {
              const Icon = ICONS[p.icon] || CheckCircle2;
              return (
                <Reveal key={p.title} delay={i * 0.04}>
                  <AccordionItem value={`item-${i}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 sm:px-6 data-[state=open]:shadow-lg transition-shadow" data-testid={`priority-item-${i}`}>
                    <AccordionTrigger className="hover:no-underline py-5" data-testid={`priority-trigger-${i}`}>
                      <div className="flex items-center gap-4 text-left">
                        <div className="w-12 h-12 rounded-xl hero-gradient flex items-center justify-center text-white shrink-0">
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="font-serif text-lg sm:text-xl font-bold text-navy">{p.title}</div>
                          <div className="text-sm text-slate-500 font-normal">{p.summary}</div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <ul className="space-y-3 pl-16">
                        {p.points.map((pt, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-slate-700">
                            <CheckCircle2 className="w-5 h-5 text-brightblue mt-0.5 shrink-0" />
                            <span>{pt}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Reveal>
              );
            })}
          </Accordion>
        </div>
      </Section>

      {/* FEATURE BAND — Community */}
      <Section className="pb-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center bg-sky/50 border border-sky-border rounded-3xl p-6 sm:p-10">
          <Reveal>
            <img src="/priorities-community.png" alt="Satish Chapagain gathered with neighbors and families in a backyard at sunset" className="rounded-3xl shadow-xl w-full object-cover aspect-[4/5]" data-testid="priorities-community-img" />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>People First. Community Always.</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy leading-tight">These priorities come from our neighbors</h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">Every item on this list started as a conversation on a front porch, in a backyard, or at a kitchen table across District 7A. When we put people first, everyone has a chance to thrive.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-brightblue hover:bg-brightblue-hover text-white rounded-full font-semibold" data-testid="priorities-involve-btn">
                <Link to="/get-involved">Get Involved <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-navy/30 text-navy hover:bg-white rounded-full font-semibold" data-testid="priorities-contact-btn">
                <Link to="/contact">Share Your Idea</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
